import { AgGridReact } from "ag-grid-react";
import { MouseEvent, useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, ALLOWED_ITEM_ACCOUNTS } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";

import { CellEditingStoppedEvent, ColDef, PostSortRowsParams, RowClickedEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import { GridCalendar } from "src/components/common/form/GridCalendar";
import { createCalendarCellClickHandler, CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useRowSelectionHandler, createCellCssClassRules } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell  } from "src/utils/grid/gridStyleUtils";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import { showWarning } from "src/utils/toast/toastUtil";
import { defaultKSK2010_R01Dto, KSK2010_R01Dto } from "./Dto_KSK2010_R01";
import { useKSK2010_R01Store } from "./Store_KSK2010_R01";
import { getItemPopupList } from "src/apis/itemApi";
import { headerNames_KSK2010_R01, isDuplicateChildItemcd, restoreOriginalValues, validateItemAccount } from './Util_KSK2010_R01';
import { useKSK2010_R01Modal } from "./hooks/KSK2010_R01Modal";
import { useKSK2010_R01CellChange } from "./hooks/KSK2010_R01CellChange";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/useDetailTableEvents";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { getMenuComponent } from "src/utils/menu/menuUtil";
import { CommonCodeColumn, CompCdColumn, WorkPlaceColumn } from "src/utils/grid/GridColumnContent";
import { cellDateComparator, onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";

/**
 * 작업지시 조회 테이블
 */

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<KSK2010_R01Dto> | null>;
}

const COLUMN_STATE_KEY = 'KSK2010_R01-grid-column-state';

export const Table_KSK2010_R01 = (props: Props) => {

	const { 
		clickable, setClickable,
		gridRef,
	} = props;

    const { 
		KSK2010_R01List, setKSK2010_R01List,
		insertKSK2010_R01List,
		removeKSK2010_R01List,
		updateKSK2010_R01List,
		selectedKSK2010_R01List, setSelectedKSK2010_R01List, initSelectedKSK2010_R01List,
		deletedKSK2010_R01List, removeDeletedKSK2010_R01List, addDeletedKSK2010_R01List,
        columnState, setColumnState,
		originalKSK2010_R01List, setOriginalKSK2010_R01List,
		isDisabled,
	} = useKSK2010_R01Store();

    const { compCdList, jobTeamList, orgCdList, ordTypeList, workProcessList, workPlaceList, unitList } = useCommonCodeStore();
	const { addTabList, setActiveTab, setProductWorkNavigationData } = useUiuxStore();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    //const gridRef = useRef<AgGridReact>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		handleSwitchButtonHandler,
		ensureSelectedRow,
	} = useGridSettingContent<KSK2010_R01Dto>({
		gridRef,
		key: 'productOrderNo',
		gridData: KSK2010_R01List,
		defaultGridData: defaultKSK2010_R01Dto,
		selectedRow: selectedKSK2010_R01List,
	});

	const workPlaceListToUse = useMemo(() => {
		return workPlaceList.filter(opt => opt.useYn === 'Y' && opt.orderYn === 'Y');
	}, [workPlaceList]);

	const getCellStyle = (params: any, key: string) => {
		return onlyTouchGridStyle(
			key,
			'productOrderNo',
		)(params);
	}
	

	const baseColumnDefs: ColDef<KSK2010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '작업지시번호', colId: 'productOrderNo', field: 'productOrderNo', width: 150, 
			cellStyle: (params: any) => getCellStyle(params, 'productOrderNo'),
			editable: false,
		},
		{
			headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
			cellStyle: (params: any) => getCellStyle(params, 'compCd'),
			...CompCdColumn.options(compCdList, false, undefined, undefined),
		},
		{
			headerName: '작업일자', colId: 'orderDt', field: 'orderDt', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'orderDt'),
			valueFormatter: (params: any) => params.data?.orderDt ? formatDate(new Date(params.data?.orderDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '지시구분', colId: 'orderType', field: 'orderType', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'orderType'),
			cellEditor: 'agSelectCellEditor', 
            cellEditorParams: { 
				values: ordTypeList.map(opt => String(opt.minorCd)) 
			}, 
            valueFormatter: (params: any) => { 
				const codeNm = ordTypeList.find(opt => String(opt.minorCd) === String(params.value)); 
				return codeNm ? codeNm.codeNm : params.value; 
			}
		},
		{
			headerName: '작업공정', colId: 'workplaceCd', field: 'workplaceCd', width: 100, 
			cellStyle: (params: any) => getCellStyle(params, 'workplaceCd'),
			...WorkPlaceColumn.options(
				workPlaceListToUse, false, (params: any) => params.data?.compCd, undefined, undefined
			)
		},
		{
			headerName: '공정유형', colId: 'workProcess', field: 'workProcess', width: 100, 
			cellStyle: (params: any) => getCellStyle(params, 'workProcess'),
			...CommonCodeColumn.options(workProcessList, false, undefined, undefined),
		},
		{
			headerName: '작업팀', colId: 'jobTeam', field: 'jobTeam', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'jobTeam'),
			cellEditor: 'agSelectCellEditor', 
			cellEditorParams: { 
				values: jobTeamList.map(opt => String(opt.minorCd)) 
			}, 
            valueFormatter: (params: any) => { 
				const codeNm = jobTeamList.find(opt => String(opt.minorCd) === String(params.value)); 
				return codeNm ? codeNm.codeNm : params.value; 
			}
		},
		{
			headerName: '작업자', colId: 'orderEmp', field: 'orderEmp', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'orderEmp'),
		},
		{
			headerName: '작업자명', colId: 'orderEmpNm', field: 'orderEmpNm', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'orderEmpNm'),
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'itemNm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'itemSpec'),
		},
		{
			headerName: '단위', colId: 'itemUnit', field: 'itemUnit', width: 100, 
			cellStyle: (params: any) => getCellStyle(params, 'itemUnit'),
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
		{
			headerName: '수량', colId: 'orderQty', field: 'orderQty', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'orderQty'),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		{
			headerName: '생산량', colId: 'resultQty', field: 'resultQty', width: 100, 
			editable: false,
			cellStyle: (params: any) => getCellStyle(params, 'resultQty'),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		// {
		// 	headerName: '마감여부', colId: 'closeTag', field: 'closeTag', width: 100, 
		// 	editable: false,
		// },
		

		// ...IndexParameterCell(),

	], [hoveredRowIndex, deletedKSK2010_R01List, KSK2010_R01List, orgCdList, compCdList, workPlaceList, jobTeamList, ordTypeList, workProcessList]);



	const columnDefs: ColDef<KSK2010_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<KSK2010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames: headerNames_KSK2010_R01,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, baseColumnDefs]);

    //modal 설정
	const { settingCodeSearchModalProps, handlePopupClose } = useKSK2010_R01Modal({
		KSK2010_R01List,
		updateKSK2010_R01List,
		gridRef,
	});

	//cellChange 설정
	const { handleCellChange, handleItemCodeSearch, handleOrderEmpSearch } = useKSK2010_R01CellChange({
		KSK2010_R01List,
		updateKSK2010_R01List,
		originalKSK2010_R01List,
		settingCodeSearchModalProps,
		gridRef,
	});


	const handleRowClick = useCallback((e: RowClickedEvent<KSK2010_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 표와 정보가 같은지 확인 - salesEstimateNo를 우선 사용
		const currentKey = rowData.productOrderNo;
		const selectedKey = selectedKSK2010_R01List?.productOrderNo;
		const isSelected = selectedKey === currentKey;
		
		if (isSelected) {
			setSelectedKSK2010_R01List(undefined);
		} else {
			setSelectedKSK2010_R01List(rowData);
		}
	}, [selectedKSK2010_R01List, setSelectedKSK2010_R01List]);

	const productOrderNavigationProcess = useCallback((row: KSK2010_R01Dto) => {
		setProductWorkNavigationData({
			productWorkNo: '',
			productOrderNo: row.productOrderNo,
		});
		const ksk2020Component = getMenuComponent('KSK2020_R01');
		if (ksk2020Component) {
			const newTab = {
				key: 'KSK2020_R01',
				title: '생산실적 등록',
				component: ksk2020Component,
			};
			addTabList(newTab);
			setActiveTab(newTab);
		}
	}, [setProductWorkNavigationData, addTabList, setActiveTab]);

	// 더블클릭으로 KSK2020_R01 페이지로 이동
	const handleRowDoubleClick = useCallback((e: RowDoubleClickedEvent<KSK2010_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		if (!validateString(rowData.productOrderNo)) {
			showWarning('작업지시번호가 없습니다.');
			return;
		}

		productOrderNavigationProcess(rowData);
	}, [productOrderNavigationProcess]);
	
	const pinnedBottomRow = useMemo(() => ([
		{
			rowIndex: '계',
			productOrderNo: KSK2010_R01List.length.toLocaleString() + "건",
			orderQty: KSK2010_R01List.reduce((sum, account) => sum + (deletedKSK2010_R01List.includes(account.productOrderNo) ? 0 : Number(account.orderQty) || 0), 0).toLocaleString(),
			resultQty: KSK2010_R01List.reduce((sum, account) => sum + (deletedKSK2010_R01List.includes(account.productOrderNo) ? 0 : Number(account.resultQty) || 0), 0).toLocaleString(),
			editable: false,
		}
	]), [KSK2010_R01List]);

	//+- 버튼 테이블에는 보류(조회하면 select한 행을 초기화)
	// useEffect(() => { ensureSelectedRow(); }, [KSK2010_R01List, selectedKSK2010_R01List]);

    return (
		<GridContextWrapper<KSK2010_R01Dto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedKSK2010_R01List}
			defaultRowData={defaultKSK2010_R01Dto}
			baseColumnDefs={columnDefs}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<KSK2010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}			defaultColDef={{
					...defaultColDef

				}}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<KSK2010_R01Dto>({
					idKey: 'productOrderNo',
					selectedList: selectedKSK2010_R01List ? [selectedKSK2010_R01List.productOrderNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onRowDoubleClicked={handleRowDoubleClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}	
				
				// Grid_Select 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 오버 상태 이벤트를 위한 핸들러
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 (로컬스토리)	
				setColumnState={setColumnState}			//컬럼 상태 저장
			/>
		</GridContextWrapper>
	)
}